import {
    MIN_ROW_GEOMETRY_HEIGHT,
    resolveRetainedRowGeometry,
    resolveInitialRowGeometry,
    resolveRowGeometryFrameStyle,
    toPositiveRowGeometryNumber,
} from './rowGeometryFrame'
import {
    hasQuestStreamReasonLabel,
    QUEST_STREAM_WIDTH_REMEASURE_DELTA_PX,
} from './questStreamHeight'
import {
    resolveQuestStreamRenderedAnswers,
    resolveQuestStreamVisibleAnswerCount,
} from './questStreamRenderedAnswers'
import {
    answerHeightContentSignature,
    resolveHeightEstimationSource,
} from './answerHeightFingerprint'

export const MIN_QUEST_GEOMETRY_HEIGHT = MIN_ROW_GEOMETRY_HEIGHT
export const MAX_REMEMBERED_QUEST_GEOMETRIES = 600

const rememberedQuestGeometries = new Map()

const normalizedQuestGeometryContext = value => {
    const context = String(value || '').trim()
    return context || 'default'
}

const questWidthBucket = width => {
    const positiveWidth = toPositiveRowGeometryNumber(width)
    if (!positiveWidth) return null

    const bucketSize = Math.max(1, QUEST_STREAM_WIDTH_REMEASURE_DELTA_PX)
    return Math.max(bucketSize, Math.round(positiveWidth / bucketSize) * bucketSize)
}

const questAnswerGeometryPart = answer => {
    if (!answer) return null
    const signatureSource = resolveHeightEstimationSource(answer)
    return [
        answer.id,
        answer.archive ? 'archived' : 'visible',
        answerHeightContentSignature(signatureSource) || 'none',
    ]
        .filter(Boolean)
        .join('@')
}

// Memoized per quest object: cache entities are immutable (writes replace the object via the
// normalized read cache), so the revision only needs computing once per quest identity. This
// runs per row on hot paths — row-model signature rebuilds and the measured-mode
// geometryIdentity/remembered-height reads — where repeat calls on the same object dominate.
// Second input, deliberately frozen: evicted-stub answer terms read the answerHeightFingerprint
// store, which is mutable. That is safe because every fingerprint write today is paired with an
// entity write that rotates the quest identity, and LRU eviction keeping the pre-eviction
// (accurate) revision is the better behavior. A future fingerprint-remember path NOT paired
// with an entity write would be masked by this memo — pair it or invalidate here.
const questGeometryRevisionCache = new WeakMap()

export const resolveQuestGeometryRevision = quest => {
    if (!quest) return 'unknown'

    const canMemoize = typeof quest === 'object'
    if (canMemoize) {
        const memoizedRevision = questGeometryRevisionCache.get(quest)
        if (memoizedRevision != null) return memoizedRevision
    }

    const sortedAnswers = Array.isArray(quest.sorted_answers) ? quest.sorted_answers : []
    const renderedAnswers = resolveQuestStreamRenderedAnswers(quest)
    const parts = [
        quest.id,
        `count:${sortedAnswers.length}`,
        `visible:${resolveQuestStreamVisibleAnswerCount(quest)}`,
        `viewerVisit:${
            Object.prototype.hasOwnProperty.call(quest, 'viewer_last_visited_at')
                ? quest.viewer_last_visited_at || 'never'
                : `legacy-${quest.last_action_created_at || 'none'}`
        }`,
        hasQuestStreamReasonLabel(quest) ? 'reasonLabel:present' : null,
        ...renderedAnswers.map(questAnswerGeometryPart),
    ].filter(Boolean)

    const revision = parts.length ? parts.join('|') : 'unknown'
    if (canMemoize) questGeometryRevisionCache.set(quest, revision)
    return revision
}

const questGeometryKeyPrefix = (options = {}) => {
    const { questId, quest = null, isThin = false, geometryKey = null } = options
    if (!questId) return null

    return [
        `quest:${questId}`,
        `surface:${isThin ? 'thin' : 'stream'}`,
        `context:${normalizedQuestGeometryContext(geometryKey)}`,
        `revision:${resolveQuestGeometryRevision(quest)}`,
    ].join('|')
}

const questGeometryKey = (options = {}) => {
    const { width = 0 } = options
    const prefix = questGeometryKeyPrefix(options)
    if (!prefix) return null

    const widthPart = questWidthBucket(width)
    return `${prefix}|width:${widthPart || 'any'}`
}

const enforceRememberedQuestGeometryLimit = () => {
    while (rememberedQuestGeometries.size > MAX_REMEMBERED_QUEST_GEOMETRIES) {
        const oldestKey = rememberedQuestGeometries.keys().next().value
        if (!oldestKey) return
        rememberedQuestGeometries.delete(oldestKey)
    }
}

export const getRememberedQuestGeometryHeight = (options = {}) => {
    const { width = 0 } = options
    const widthPart = questWidthBucket(width)
    const exactKey = widthPart ? questGeometryKey(options) : null
    if (exactKey) return rememberedQuestGeometries.get(exactKey)?.height || null

    const prefix = questGeometryKeyPrefix(options)
    if (!prefix) return null

    let fallbackHeight = null
    for (const [key, entry] of rememberedQuestGeometries) {
        if (key.startsWith(prefix)) fallbackHeight = entry.height
    }

    return fallbackHeight
}

export const rememberRetainedQuestGeometry = (options = {}) => {
    const { height = 0 } = options
    const rememberedHeight = toPositiveRowGeometryNumber(height)
    if (!rememberedHeight) return false

    const key = questGeometryKey(options)
    if (!key) return false

    rememberedQuestGeometries.delete(key)
    rememberedQuestGeometries.set(key, { height: rememberedHeight })
    enforceRememberedQuestGeometryLimit()
    return true
}

export const resolveInitialQuestGeometry = ({
    questId = null,
    quest = null,
    estimatedHeight = 0,
    estimatedWidth = 0,
    isThin = false,
    geometryKey = null,
    useRememberedHeight = true,
} = {}) => {
    const rememberedHeight = useRememberedHeight
        ? getRememberedQuestGeometryHeight({
              questId,
              quest,
              width: estimatedWidth,
              isThin,
              geometryKey,
          })
        : null

    return resolveInitialRowGeometry({
        rememberedHeight,
        estimatedHeight,
        minHeight: MIN_QUEST_GEOMETRY_HEIGHT,
    })
}

export const resolveRetainedQuestGeometry = ({
    measuredHeight = 0,
    previousHeight = 0,
    previousMeasuredHeight = 0,
    hasMeasuredHeight = false,
    preserveHeight = false,
    allowShrink = false,
    minHeight = MIN_QUEST_GEOMETRY_HEIGHT,
} = {}) =>
    resolveRetainedRowGeometry({
        measuredHeight,
        previousHeight,
        previousMeasuredHeight,
        hasMeasuredHeight,
        preserveHeight,
        allowShrink,
        minHeight,
    })

export const resolveQuestContainerStyle = ({
    intrinsicHeight = MIN_QUEST_GEOMETRY_HEIGHT,
    shouldRender = false,
    isVirtual = false,
} = {}) =>
    resolveRowGeometryFrameStyle({
        intrinsicHeight,
        shouldRender,
        minHeight: MIN_QUEST_GEOMETRY_HEIGHT,
        renderedHeightMode: isVirtual ? 'none' : 'minHeight',
        placeholderHeightMode: 'height',
    })

export const clearRememberedQuestGeometriesForTest = () => {
    rememberedQuestGeometries.clear()
}
